<template>
  <!-- ===================================================================
       CONNEXION ET INSCRIPTION — écran de l'application.
       Le site garde son balisage d'origine dans le `v-else` plus bas.
       Les deux formulaires vivent dans le même écran, séparés par un
       sélecteur : c'est un passage obligé, pas une destination, il ne doit
       pas coûter une navigation supplémentaire.
       =================================================================== -->
  <div v-if="isNative" class="cx-page">

    <header class="cx-top">
      <h1 class="cx-top__brand">Oui<span>C</span>Sport</h1>
    </header>

    <div class="cx-switch" role="tablist" aria-label="Connexion ou inscription">
      <span class="cx-switch__marker" :class="{ 'is-right': !isLoginForm }" aria-hidden="true"></span>
      <button
        type="button" role="tab" class="cx-switch__btn"
        :class="{ 'is-on': isLoginForm }" :aria-selected="isLoginForm"
        :disabled="cxEnvoi"
        @click="isLoginForm || toggleForm()"
      >Connexion</button>
      <button
        type="button" role="tab" class="cx-switch__btn"
        :class="{ 'is-on': !isLoginForm }" :aria-selected="!isLoginForm"
        :disabled="cxEnvoi"
        @click="isLoginForm && toggleForm()"
      >Inscription</button>
    </div>

    <div class="cx-body">
      <p v-if="errorMessage" class="cx-alerte" role="alert">
        <span class="cx-alerte__mark" aria-hidden="true">!</span>
        {{ errorMessage }}
      </p>

      <!-- ---------- Connexion ---------- -->
      <form v-if="isLoginForm" class="cx-form" @submit.prevent="login">
        <div class="cx-champ">
          <label class="cx-champ__label" for="cx-email">Adresse e-mail</label>
          <input
            id="cx-email" v-model="email" type="email" inputmode="email"
            class="cx-input" placeholder="exemple@ouicsport.fr"
            autocomplete="email" required
          >
        </div>

        <div class="cx-champ">
          <label class="cx-champ__label" for="cx-mdp">Mot de passe</label>
          <div class="cx-input-wrap">
            <input
              id="cx-mdp" v-model="password" :type="cxVoirMdp ? 'text' : 'password'"
              class="cx-input cx-input--icone" placeholder="Votre mot de passe"
              autocomplete="current-password" required
            >
            <button
              type="button" class="cx-oeil"
              :aria-label="cxVoirMdp ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
              @click="cxVoirMdp = !cxVoirMdp"
            >
              <svg v-if="!cxVoirMdp" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1.5 12S5.5 4.5 12 4.5 22.5 12 22.5 12 18.5 19.5 12 19.5 1.5 12 1.5 12z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17.9 17.9A10 10 0 0 1 12 19.5C5.5 19.5 1.5 12 1.5 12a18.4 18.4 0 0 1 5-5.9M9.9 4.8A9.1 9.1 0 0 1 12 4.5c6.5 0 10.5 7.5 10.5 7.5a18.5 18.5 0 0 1-2.2 3.2m-6.7-1.1a3 3 0 1 1-4.2-4.2"/><path d="M2 2l20 20"/></svg>
            </button>
          </div>
        </div>

        <div class="cx-options">
          <label class="cx-case">
            <input v-model="cxSeSouvenir" type="checkbox">
            <span class="cx-case__box" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"/></svg>
            </span>
            Se souvenir de moi
          </label>
          <RouterLink to="/forgot-password" class="cx-oubli">Mot de passe oublié ?</RouterLink>
        </div>

        <button type="submit" class="cx-envoi" :disabled="cxEnvoi">
          <span v-if="cxEnvoi" class="cx-envoi__roue" aria-hidden="true"></span>
          {{ cxEnvoi ? 'Connexion…' : 'Se connecter' }}
        </button>

        <p class="cx-bascule">
          Pas encore de compte ?
          <button type="button" class="cx-bascule__lien" :disabled="cxEnvoi" @click="toggleForm">S'inscrire</button>
        </p>
      </form>

      <!-- ---------- Inscription ---------- -->
      <form v-else class="cx-form" @submit.prevent="register">
        <div class="cx-duo">
          <div class="cx-champ">
            <label class="cx-champ__label" for="cx-prenom">Prénom</label>
            <input id="cx-prenom" v-model="firstname" type="text" class="cx-input" placeholder="Alex" autocomplete="given-name" required>
          </div>
          <div class="cx-champ">
            <label class="cx-champ__label" for="cx-nom">Nom</label>
            <input id="cx-nom" v-model="lastname" type="text" class="cx-input" placeholder="Dupont" autocomplete="family-name" required>
          </div>
        </div>

        <div class="cx-champ">
          <label class="cx-champ__label" for="cx-email-i">Adresse e-mail</label>
          <input id="cx-email-i" v-model="email" type="email" inputmode="email" class="cx-input" placeholder="alex.dupont@email.fr" autocomplete="email" required>
        </div>

        <div class="cx-champ">
          <label class="cx-champ__label" for="cx-tel">Téléphone</label>
          <input id="cx-tel" v-model="phone" type="tel" inputmode="tel" class="cx-input" placeholder="06 12 34 56 78" autocomplete="tel" required @input="formatPhoneNumber">
        </div>

        <div class="cx-champ">
          <label class="cx-champ__label" for="cx-mdp-i">Mot de passe</label>
          <div class="cx-input-wrap">
            <input id="cx-mdp-i" v-model="password" :type="cxVoirMdp2 ? 'text' : 'password'" class="cx-input cx-input--icone" placeholder="8 caractères minimum" autocomplete="new-password" minlength="8" required>
            <button type="button" class="cx-oeil" :aria-label="cxVoirMdp2 ? 'Masquer' : 'Afficher'" @click="cxVoirMdp2 = !cxVoirMdp2">
              <svg v-if="!cxVoirMdp2" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1.5 12S5.5 4.5 12 4.5 22.5 12 22.5 12 18.5 19.5 12 19.5 1.5 12 1.5 12z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17.9 17.9A10 10 0 0 1 12 19.5C5.5 19.5 1.5 12 1.5 12a18.4 18.4 0 0 1 5-5.9M9.9 4.8A9.1 9.1 0 0 1 12 4.5c6.5 0 10.5 7.5 10.5 7.5a18.5 18.5 0 0 1-2.2 3.2m-6.7-1.1a3 3 0 1 1-4.2-4.2"/><path d="M2 2l20 20"/></svg>
            </button>
          </div>
        </div>

        <div class="cx-champ">
          <label class="cx-champ__label" for="cx-mdp-c">Confirmation</label>
          <div class="cx-input-wrap">
            <input id="cx-mdp-c" v-model="confirmPassword" :type="cxVoirMdp3 ? 'text' : 'password'" class="cx-input cx-input--icone" placeholder="Répéter le mot de passe" autocomplete="new-password" required>
            <button type="button" class="cx-oeil" :aria-label="cxVoirMdp3 ? 'Masquer' : 'Afficher'" @click="cxVoirMdp3 = !cxVoirMdp3">
              <svg v-if="!cxVoirMdp3" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1.5 12S5.5 4.5 12 4.5 22.5 12 22.5 12 18.5 19.5 12 19.5 1.5 12 1.5 12z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17.9 17.9A10 10 0 0 1 12 19.5C5.5 19.5 1.5 12 1.5 12a18.4 18.4 0 0 1 5-5.9M9.9 4.8A9.1 9.1 0 0 1 12 4.5c6.5 0 10.5 7.5 10.5 7.5a18.5 18.5 0 0 1-2.2 3.2m-6.7-1.1a3 3 0 1 1-4.2-4.2"/><path d="M2 2l20 20"/></svg>
            </button>
          </div>
          <span v-if="Boolean(confirmPassword) && password !== confirmPassword" class="cx-champ__erreur">
            Les deux mots de passe ne correspondent pas.
          </span>
        </div>

        <label class="cx-case cx-case--cgu">
          <input v-model="cxConditions" type="checkbox" required>
          <span class="cx-case__box" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"/></svg>
          </span>
          <span class="cx-case__texte">
            J'accepte la
            <RouterLink to="/politique-de-confidentialite">politique de confidentialité</RouterLink>
            et les
            <RouterLink to="/CGU">conditions d'utilisation</RouterLink>.
          </span>
        </label>

        <button type="submit" class="cx-envoi" :disabled="cxEnvoi || Boolean(confirmPassword) && password !== confirmPassword">
          <span v-if="cxEnvoi" class="cx-envoi__roue" aria-hidden="true"></span>
          {{ cxEnvoi ? 'Création…' : 'Créer mon compte' }}
        </button>

        <p class="cx-bascule">
          Déjà inscrit ?
          <button type="button" class="cx-bascule__lien" :disabled="cxEnvoi" @click="toggleForm">Se connecter</button>
        </p>
      </form>
    </div>

    <transition name="fade">
      <p v-if="showToast" class="cx-info">{{ toastMessage }}</p>
    </transition>
  </div>

  <!-- ===== Site web : balisage d'origine, strictement inchangé ===== -->
  <template v-else>

  <transition name="fade">
    <div class="toast" v-if="showToast">
      <p>{{ toastMessage }}</p>
    </div>
  </transition>

  <section class="hero2"></section>

  <div class="signup-container" v-if="!isLoginForm">
    <h2 style="font-family: 'gagalin';color:#e74c3c; margin-bottom: 15px; font-size: clamp(1.5rem, 2vw, 2rem);">INSCRIVEZ-VOUS</h2>
    <form action="/signup" method="post" class="signup-form" @submit.prevent="register">
      <div class="form-row">
        <div class="input-group">
          <input v-model="lastname" type="text" id="lastname" name="lastname" class="oc-field__control" placeholder="Nom" required>
        </div>
        <div class="input-group">
          <input v-model="firstname" type="text" id="firstname" name="firstname" class="oc-field__control" placeholder="Prénom" required>
        </div>
      </div>
      <div class="form-row">
        <div class="input-group">
          <input v-model="email" type="email" id="email" name="email" class="oc-field__control" placeholder="Adresse e-mail" required>
        </div>
        <div class="input-group">
          <input v-model="phone" type="tel" id="phone" name="phone" class="oc-field__control" placeholder="Numéro de téléphone" required @input="formatPhoneNumber">
        </div>
      </div>
      <div class="form-row">
        <div class="input-group">
          <input v-model="password" type="password" id="password" name="password" class="oc-field__control" placeholder="Mot de passe" required>
        </div>
        <div class="input-group">
          <input v-model="confirmPassword" type="password" id="confirm_password" name="confirm_password" class="oc-field__control" placeholder="Confirmation du mot de passe" required>
        </div>
      </div>
      <div class="login-footer">
        <a class="" @click.prevent="toggleForm">Déjà un compte ?</a>
      </div>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      <button type="submit" class="btn signup-button" @click.prevent="register">S'inscrire</button>
      <p class="privacy-policy">Vos données sont traitées par Ouicsport pour permettre la création de votre compte, gérer et optimiser la relation clientèle. Pour plus de précisions, voir notre <a href="/admin-website/politique-de-confidentialite">Politique de confidentialité</a>.</p>
    </form>
  </div>

  <div class="login-container" v-if="isLoginForm">
    <h2 style="font-family: 'gagalin';color:#e74c3c; margin-bottom: 15px; font-size: clamp(1.5rem, 2vw, 2rem);">CONNECTEZ-VOUS</h2>
    <form action="/login" method="post" class="login-form" @submit.prevent="login">
      <div class="input-group">
        <input v-model="email" type="email" id="email" name="email" class="oc-field__control" placeholder="Adresse e-mail" required>
      </div>
      <div class="input-group">
        <input v-model="password" type="password" id="password" name="password" class="oc-field__control" placeholder="Mot de passe" required>
      </div>
      <div class="aditional">
        <div class="checkbox-container">
          <input type="checkbox" id="remember-me">
          <label for="remember-me" class="check-login">Souvenir de moi</label>
        </div>
        <a href="/forgot-password" class="password check-login">Mot de passe oublié ?</a>
      </div>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      <button type="submit" class="btn login-button" @click.prevent="login">Se connecter</button>
      <div class="login-footer">
        <a class="" @click.prevent="toggleForm">Pas encore de compte ?</a>
      </div>
    </form>
  </div>
  </template>
</template>

<script>
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { isNative } from '@/api/base'

export default {
  setup() {
    const store = useStore()
    const router = useRouter()
    const lastname = ref('')
    const firstname = ref('')
    const email = ref('')
    const phone = ref('')
    const password = ref('')
    const confirmPassword = ref('')
    const errorMessage = ref('')
    const isLoginForm = ref(true)
    const showToast = ref(false)
    const toastMessage = ref('')

    /* ---- État propre à l'écran natif ---- */
    const cxEnvoi = ref(false)          // envoi en cours : bloque le bouton
    const cxVoirMdp = ref(false)        // afficher le mot de passe (connexion)
    const cxVoirMdp2 = ref(false)       // idem, inscription
    const cxVoirMdp3 = ref(false)       // idem, confirmation
    const cxSeSouvenir = ref(true)
    const cxConditions = ref(true)

    const formatPhoneNumber = (event) => {
      let raw = event.target.value.replace(/\D/g, '')
      if (raw.length > 10) raw = raw.slice(0, 10)
      let formatted = ''
      for (let i = 0; i < raw.length; i += 2) {
        if (i > 0) formatted += ' '
        formatted += raw.substring(i, i + 2)
      }
      phone.value = formatted
    }

    const login = async () => {
      cxEnvoi.value = true
      try {
        await store.dispatch('auth/login', { email: email.value, password: password.value })
        errorMessage.value = ''
        router.push('/')
      } catch (error) {
        const message = error.response?.data?.message ? error.response.data.message : 'Une erreur est survenue lors de la connexion.'
        errorMessage.value = message
      } finally {
        cxEnvoi.value = false
      }
    }

    const register = async () => {
      cxEnvoi.value = true
      try {
        await store.dispatch('auth/register', {
          lastname: lastname.value,
          firstname: firstname.value,
          email: email.value,
          phone: phone.value,
          password: password.value
        })
        errorMessage.value = ''
        toastMessage.value = 'Inscription réussie, vérifiez vos e-mails pour activer votre compte.'
        showToast.value = true
        setTimeout(() => {
          showToast.value = false
          router.push('/login')
        }, 3000)
      } catch (error) {
        errorMessage.value = error.message || "Une erreur est survenue lors de l'enregistrement."
      } finally {
        cxEnvoi.value = false
      }
    }

    const toggleForm = () => {
      isLoginForm.value = !isLoginForm.value
      // Une erreur de connexion n'a plus de sens sur le formulaire d'inscription.
      errorMessage.value = ''
    }

    return {
      cxEnvoi, cxVoirMdp, cxVoirMdp2, cxVoirMdp3, cxSeSouvenir, cxConditions,
      lastname,
      firstname,
      email,
      phone,
      password,
      confirmPassword,
      errorMessage,
      isLoginForm,
      toggleForm,
      formatPhoneNumber,
      login,
      register,
      showToast,
      toastMessage,
      isNative
    }
  },
}
</script>

<style>
/* =========================================================================
   Styles du SITE WEB — restaurés à l'identique.
   =========================================================================
   Ils vivaient dans le bloc <style> remplacé par la feuille de l'application.
   Le contrôle de non-régression du CSS web les a signalés manquants ; ils
   sont repris tels quels depuis l'empreinte de référence.
   Ne pas y toucher : ils n'ont d'effet que sur le site.
   ========================================================================= */
.fade-enter-active,.fade-leave-active {transition:opacity .3s}
.fade-enter-from,.fade-leave-to {opacity:0}
.toast {position:fixed;top:2rem;right:2rem;z-index:9999;background:#fefefe;border:1px solid #ccc;padding:1rem 1.5rem;border-radius:4px;box-shadow:0 4px 6px rgba(0,0,0,.15);font-size:1rem}
</style>

<style>
/* =========================================================================
   CONNEXION ET INSCRIPTION — feuille de l'application, écrite de zéro
   =========================================================================
   Balisage natif distinct de celui du site : rien n'est hérité ni combattu.
   Sélecteurs préfixés `body.is-native`, ce qui permet au contrôle automatique
   de vérifier que le site reste intact.
   ========================================================================= */

body.is-native .cx-page,
body.is-native .cx-page *,
body.is-native .cx-page *::before,
body.is-native .cx-page *::after {
  box-sizing: border-box;
}

body.is-native .cx-page header,
body.is-native .cx-page h1,
body.is-native .cx-page p,
body.is-native .cx-page span,
body.is-native .cx-page div,
body.is-native .cx-page form,
body.is-native .cx-page label,
body.is-native .cx-page button,
body.is-native .cx-page a {
  all: unset;
  display: revert;
  box-sizing: border-box;
}

body.is-native .cx-page {
  --cx-bg: #F5F0E6;
  --cx-surface: #EFE8DA;
  --cx-raised: #E5DDD0;
  --cx-ink: #28201C;
  --cx-ink-2: #6B5E54;
  --cx-ink-3: #8C7E73;
  --cx-on-dark: #FAF6EE;
  --cx-red: #DD4124;
  --cx-hair: rgba(107, 94, 84, 0.18);
  --cx-err-fond: #F9E2DF;
  --cx-err-trait: #E89A90;
  --cx-err-texte: #B8250E;

  min-height: 100%;
  padding-top: calc(env(safe-area-inset-top, 0px) + 16px);
  /* Marge basse généreuse : le clavier occupe la moitié de l'écran, le bouton
     d'envoi ne doit jamais se retrouver coincé dessous. */
  padding-bottom: calc(var(--oc-sp-12) + var(--oc-sp-8));
  background: var(--cx-bg);
  font-family: 'Dosis', system-ui, sans-serif;
  color: var(--cx-ink);
}

body.is-native .cx-page .cx-top { padding: 0 20px 16px; }

body.is-native .cx-page .cx-top__brand {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 28px;
  line-height: 1;
  letter-spacing: 0.5px;
  color: var(--cx-ink);
}

body.is-native .cx-page .cx-top__brand span { color: var(--cx-red); }

/* ---------- Sélecteur connexion / inscription ---------- */

body.is-native .cx-page .cx-switch {
  position: relative;
  display: flex;
  margin: 0 20px 18px;
  padding: 4px;
  background: var(--cx-surface);
  border: 1px solid var(--cx-hair);
  border-radius: 16px;
}

body.is-native .cx-page .cx-switch__marker {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc(50% - 4px);
  background: var(--cx-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(40, 32, 28, 0.12);
  transition: transform 250ms cubic-bezier(0.2, 0, 0, 1);
}

body.is-native .cx-page .cx-switch__marker.is-right { transform: translateX(100%); }

body.is-native .cx-page .cx-switch__btn {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 40px;
  font-size: 16px;
  font-weight: 800;
  color: var(--cx-ink-2);
  border-radius: 12px;
  cursor: pointer;
  transition: color 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cx-page .cx-switch__btn.is-on { color: var(--cx-ink); }

/* ---------- Formulaire ---------- */

body.is-native .cx-page .cx-body { padding: 0 20px; }

body.is-native .cx-page .cx-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

body.is-native .cx-page .cx-duo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

body.is-native .cx-page .cx-champ {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

body.is-native .cx-page .cx-champ__label {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--cx-ink);
}

body.is-native .cx-page .cx-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

body.is-native .cx-page .cx-input {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  /* 16px minimum : en dessous, iOS agrandit tout seul la page à la mise au
     point du champ, et l'écran part de travers. */
  font-family: 'Dosis', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--cx-ink);
  background: var(--cx-surface);
  border: 1.5px solid var(--cx-hair);
  border-radius: 14px;
  -webkit-user-select: text;
  user-select: text;
  transition: border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              background 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cx-page .cx-input::placeholder {
  color: var(--cx-ink-3);
  font-weight: 500;
}

body.is-native .cx-page .cx-input:focus {
  border-color: var(--cx-red);
  background: #FAF5EB;
}

body.is-native .cx-page .cx-input--icone { padding-right: 48px; }

body.is-native .cx-page .cx-oeil {
  position: absolute;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: var(--cx-ink-3);
  border-radius: 12px;
  cursor: pointer;
}

body.is-native .cx-page .cx-oeil:active { color: var(--cx-ink); }

body.is-native .cx-page .cx-champ__erreur {
  font-size: 13px;
  font-weight: 700;
  color: var(--cx-err-texte);
}

/* ---------- Options ---------- */

body.is-native .cx-page .cx-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
}

body.is-native .cx-page .cx-case {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  font-size: 15px;
  font-weight: 600;
  color: var(--cx-ink);
  cursor: pointer;
}

body.is-native .cx-page .cx-case--cgu {
  align-items: flex-start;
  margin-top: 4px;
}

body.is-native .cx-page .cx-case input { display: none; }

body.is-native .cx-page .cx-case__box {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  color: transparent;
  background: var(--cx-surface);
  border: 1.5px solid rgba(107, 94, 84, 0.32);
  border-radius: 7px;
  transition: background 150ms cubic-bezier(0.2, 0.8, 0.2, 1),
              border-color 150ms cubic-bezier(0.2, 0.8, 0.2, 1),
              color 150ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cx-page .cx-case--cgu .cx-case__box { margin-top: 11px; }

body.is-native .cx-page .cx-case input:checked + .cx-case__box {
  color: var(--cx-on-dark);
  background: var(--cx-red);
  border-color: var(--cx-red);
}

body.is-native .cx-page .cx-case__texte {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--cx-ink-2);
}

body.is-native .cx-page .cx-case__texte a {
  color: var(--cx-red);
  text-decoration: underline;
  cursor: pointer;
}

body.is-native .cx-page .cx-oubli {
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 700;
  color: var(--cx-red);
  cursor: pointer;
}

/* ---------- Envoi ---------- */

body.is-native .cx-page .cx-envoi {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  margin-top: 8px;
  font-size: 18px;
  font-weight: 800;
  color: var(--cx-on-dark);
  background: var(--cx-red);
  border-radius: 26px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(221, 65, 36, 0.32);
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              opacity 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cx-page .cx-envoi:active { transform: scale(0.97); }

body.is-native .cx-page .cx-envoi:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

body.is-native .cx-page .cx-envoi__roue {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(250, 246, 238, 0.35);
  border-top-color: var(--cx-on-dark);
  border-radius: 50%;
  animation: cx-tourne 0.8s linear infinite;
}

@keyframes cx-tourne {
  to { transform: rotate(360deg); }
}

body.is-native .cx-page .cx-bascule {
  margin-top: 16px;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  color: var(--cx-ink-2);
}

body.is-native .cx-page .cx-bascule__lien {
  margin-left: 4px;
  font-size: 15px;
  font-weight: 800;
  color: var(--cx-red);
  text-decoration: underline;
  cursor: pointer;
}

/* ---------- Messages ---------- */

body.is-native .cx-page .cx-alerte {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--cx-err-texte);
  background: var(--cx-err-fond);
  border: 1px solid var(--cx-err-trait);
  border-radius: 14px;
}

body.is-native .cx-page .cx-alerte__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  font-size: 13px;
  font-weight: 800;
  color: var(--cx-err-fond);
  background: var(--cx-err-texte);
  border-radius: 10px;
}

body.is-native .cx-page .cx-info {
  position: fixed;
  left: 20px;
  right: 20px;
  bottom: calc(var(--oc-tabbar-h) + var(--oc-safe-bottom) + 16px);
  z-index: 1000;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
  color: var(--cx-on-dark);
  background: #2B231F;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(26, 22, 20, 0.28);
}

@media (prefers-reduced-motion: reduce) {
  body.is-native .cx-page .cx-envoi__roue { animation: none; }
  body.is-native .cx-page .cx-switch__marker { transition: none; }
}
</style>
